import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import intakeApi from './intakeApi';
import './intake.css';

const TABS = [
  'Overview', 'Patient', 'Coverage', 'Encounter', 'Claim Lines',
  'Denials', 'Clinical Evidence', 'Documents', 'Missing Items', 'Raw JSON',
];

const STATUS_OPTIONS = ['missing', 'provided', 'resolved', 'dismissed'];

const ARCHETYPE_OPTIONS = [
  'dx_proc_inconsistency', 'cob_crossover', 'medical_necessity',
  'coding_bundling', 'drg_downgrade',
];
const COMPLEXITY_OPTIONS = ['Simple', 'Medium', 'Expert'];
const REQUESTED_ACTION_OPTIONS = ['file_appeal', 'corrected_claim'];

/** Immutable nested set: setPath(obj, ['a','b'], v) -> new obj. */
function setPath(obj, path, value) {
  if (!path.length) return value;
  const [head, ...rest] = path;
  const base = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  base[head] = setPath(base[head], rest, value);
  return base;
}

function get(obj, path, fallback = '') {
  let cur = obj;
  for (const key of path) {
    if (cur == null) return fallback;
    cur = cur[key];
  }
  return cur == null ? fallback : cur;
}

/** ["a","b"] <-> "a, b" so users can edit code lists in a single input. */
function arrToComma(arr) {
  if (!Array.isArray(arr)) return arr ?? '';
  return arr.join(', ');
}
function commaToArr(str) {
  return String(str || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <>
      <div className="intake-kv__key">{label}</div>
      <div className="intake-kv__val">
        <input
          className="intake-input"
          type={type}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </>
  );
}

function SelectField({ label, value, options, onChange, allowEmpty = true }) {
  return (
    <>
      <div className="intake-kv__key">{label}</div>
      <div className="intake-kv__val">
        <select
          className="intake-input"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          {allowEmpty && <option value="">—</option>}
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    </>
  );
}

function TextAreaField({ label, value, onChange, rows = 4, placeholder }) {
  return (
    <>
      <div className="intake-kv__key">{label}</div>
      <div className="intake-kv__val">
        <textarea
          className="intake-input intake-textarea"
          rows={rows}
          placeholder={placeholder}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </>
  );
}

export default function IntakeCasePage() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('Overview');
  const [caseRow, setCaseRow] = useState(null);
  const [cmJson, setCmJson] = useState({});
  const [documents, setDocuments] = useState([]);
  const [missingItems, setMissingItems] = useState([]);
  const [rawText, setRawText] = useState('');
  const [rawError, setRawError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null); // {type, text}
  const [enginePreference, setEnginePreference] = useState('v5');
  const docInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await intakeApi.getCase(caseId);
      if (!data?.success) throw new Error(data?.message || 'Case not found');
      setCaseRow(data.case || null);
      setCmJson(data.cmJson || {});
      setDocuments(data.documents || []);
      setMissingItems(data.missingItems || []);
      setRawText(JSON.stringify(data.cmJson || {}, null, 2));
    } catch (err) {
      setBanner({ type: 'error', text: err?.response?.data?.message || err.message });
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const visibility = caseRow?.Visibility || 'private';

  const update = (path, value) => setCmJson((prev) => setPath(prev, path, value));

  // ---- Array helpers (claim lines / denials / clinical evidence) ----
  const addItem = (key, blank) =>
    setCmJson((prev) => ({ ...prev, [key]: [...(prev[key] || []), blank] }));
  const removeItem = (key, idx) =>
    setCmJson((prev) => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== idx) }));

  const toggleRequestedAction = (action) =>
    setCmJson((prev) => {
      const current = Array.isArray(prev.requested_actions) ? prev.requested_actions : [];
      const next = current.includes(action)
        ? current.filter((a) => a !== action)
        : [...current, action];
      return { ...prev, requested_actions: next };
    });

  const save = async () => {
    setSaving(true);
    setBanner(null);
    try {
      let payload = cmJson;
      // If the user edited Raw JSON, prefer that (validated).
      if (activeTab === 'Raw JSON') {
        try {
          payload = JSON.parse(rawText);
          setCmJson(payload);
          setRawError('');
        } catch (e) {
          setRawError('Invalid JSON — fix it before saving.');
          setSaving(false);
          return;
        }
      }
      const data = await intakeApi.updateCase(caseId, { cmJson: payload, missingItems });
      if (!data?.success) throw new Error(data?.message || 'Save failed');
      setCmJson(data.cmJson || payload);
      setRawText(JSON.stringify(data.cmJson || payload, null, 2));
      if (data.missingItems) setMissingItems(data.missingItems);
      setBanner({ type: 'success', text: 'Saved.' });
    } catch (err) {
      setBanner({ type: 'error', text: err?.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = async () => {
    const next = visibility === 'global' ? 'private' : 'global';
    try {
      const data = await intakeApi.setVisibility(caseId, next);
      if (!data?.success) throw new Error(data?.message || 'Failed');
      setCaseRow((prev) => ({ ...(prev || {}), Visibility: next }));
      setBanner({ type: 'success', text: `Moved to the ${next} queue.` });
    } catch (err) {
      setBanner({ type: 'error', text: err?.response?.data?.message || err.message });
    }
  };

  const onAddDocuments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBanner({ type: 'info', text: 'Uploading documents…' });
    try {
      const data = await intakeApi.addDocuments(caseId, files);
      if (!data?.success) throw new Error(data?.message || 'Upload failed');
      setDocuments(data.documents || []);
      setBanner({ type: 'success', text: `Added ${data.addedCount} document(s).` });
    } catch (err) {
      setBanner({ type: 'error', text: err?.response?.data?.message || err.message });
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setBanner(null);
    try {
      const data = await intakeApi.submit(caseId, { engine: enginePreference });
      if (!data?.success) throw new Error(data?.message || 'Submit failed');
      navigate(`/denials/${caseId}`, { state: { engine: enginePreference, autostart: true } });
    } catch (err) {
      setBanner({ type: 'error', text: err?.response?.data?.message || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const setItemStatus = (id, status) =>
    setMissingItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));

  const requiredOutstanding = useMemo(
    () => missingItems.filter((it) => it.required && it.status === 'missing').length,
    [missingItems],
  );

  if (loading) return <div className="intake-page"><div className="intake-empty">Loading…</div></div>;

  const claimLines = cmJson.claim_lines || [];
  const denials = cmJson.denials || [];
  const clinicalEvidence = cmJson.clinical_evidence || [];
  const hasClinicalNote = clinicalEvidence.some((ev) => String(ev?.body || '').trim());
  const requestedActions = Array.isArray(cmJson.requested_actions) ? cmJson.requested_actions : [];

  return (
    <div className="intake-page">
      <div className="intake-page__head">
        <div>
          <h1 className="intake-page__title">
            {caseRow?.ExternalId || `Case ${caseId.slice(0, 8)}`}
          </h1>
          <p className="intake-page__subtitle">
            <span className={`intake-pill intake-pill--${visibility}`}>{visibility}</span>
            &nbsp;· {requiredOutstanding} required item(s) outstanding
          </p>
        </div>
        <div className="intake-actions">
          <button type="button" className="intake-btn intake-btn--ghost" onClick={() => navigate('/intake')}>
            Queue
          </button>
          <button type="button" className="intake-btn intake-btn--ghost" onClick={toggleVisibility}>
            {visibility === 'global' ? 'Make private' : 'Move to global'}
          </button>
          <button type="button" className="intake-btn" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="intake-btn"
            disabled={submitting || requiredOutstanding > 0}
            title={requiredOutstanding > 0 ? 'Resolve required items before submitting' : ''}
            onClick={submit}
          >
            {submitting ? 'Submitting…' : 'Submit to workflow'}
          </button>
        </div>
      </div>

      {banner && <div className={`intake-banner intake-banner--${banner.type}`}>{banner.text}</div>}

      <div className="intake-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`intake-tab${activeTab === t ? ' is-active' : ''}`}
            onClick={() => setActiveTab(t)}
          >
            {t}
            {t === 'Missing Items' && requiredOutstanding > 0 ? ` (${requiredOutstanding})` : ''}
            {t === 'Clinical Evidence' && !hasClinicalNote ? ' *' : ''}
          </button>
        ))}
      </div>

      {activeTab === 'Overview' && (
        <div className="intake-card">
          <div className="intake-kv">
            <Field label="Case id" value={get(cmJson, ['case_id'])}
                   onChange={(v) => update(['case_id'], v)} />
            <Field label="Claim number" value={get(cmJson, ['claim_number'])}
                   onChange={(v) => update(['claim_number'], v)} />
            <SelectField label="Archetype" value={get(cmJson, ['archetype'])}
                   options={ARCHETYPE_OPTIONS} onChange={(v) => update(['archetype'], v)} />
            <SelectField label="Complexity" value={get(cmJson, ['complexity_level'])}
                   options={COMPLEXITY_OPTIONS} onChange={(v) => update(['complexity_level'], v)} />
            <Field label="Payer name" value={get(cmJson, ['coverage', 'payer_name'])}
                   onChange={(v) => update(['coverage', 'payer_name'], v)} />
            <Field label="Payer id" value={get(cmJson, ['coverage', 'payer_id'])}
                   onChange={(v) => update(['coverage', 'payer_id'], v)} />
            <Field label="Denied date from" value={get(cmJson, ['denied_date_from'])}
                   onChange={(v) => update(['denied_date_from'], v)} />
            <Field label="Denied date to" value={get(cmJson, ['denied_date_to'])}
                   onChange={(v) => update(['denied_date_to'], v)} />
            <div className="intake-kv__key">Requested actions</div>
            <div className="intake-kv__val">
              <div className="intake-checkrow">
                {REQUESTED_ACTION_OPTIONS.map((a) => (
                  <label key={a} className="intake-check">
                    <input
                      type="checkbox"
                      checked={requestedActions.includes(a)}
                      onChange={() => toggleRequestedAction(a)}
                    />
                    {a}
                  </label>
                ))}
              </div>
            </div>
            <TextAreaField label="Case summary" value={get(cmJson, ['summary'])}
                   rows={5}
                   placeholder="Plain-text summary of the denial, what happened, and the appeal angle."
                   onChange={(v) => update(['summary'], v)} />
          </div>
        </div>
      )}

      {activeTab === 'Patient' && (
        <div className="intake-card">
          <div className="intake-kv">
            <Field label="External id" value={get(cmJson, ['patient', 'external_id'])}
                   onChange={(v) => update(['patient', 'external_id'], v)} />
            <Field label="First name" value={get(cmJson, ['patient', 'first_name'])}
                   onChange={(v) => update(['patient', 'first_name'], v)} />
            <Field label="Last name" value={get(cmJson, ['patient', 'last_name'])}
                   onChange={(v) => update(['patient', 'last_name'], v)} />
            <Field label="Date of birth" value={get(cmJson, ['patient', 'date_of_birth'])}
                   onChange={(v) => update(['patient', 'date_of_birth'], v)} />
            <Field label="Sex" value={get(cmJson, ['patient', 'sex'])}
                   onChange={(v) => update(['patient', 'sex'], v)} />
          </div>
        </div>
      )}

      {activeTab === 'Coverage' && (
        <div className="intake-card">
          <div className="intake-kv">
            <Field label="Payer name" value={get(cmJson, ['coverage', 'payer_name'])}
                   onChange={(v) => update(['coverage', 'payer_name'], v)} />
            <Field label="Payer id" value={get(cmJson, ['coverage', 'payer_id'])}
                   onChange={(v) => update(['coverage', 'payer_id'], v)} />
            <Field label="Plan type" value={get(cmJson, ['coverage', 'plan_type'])}
                   onChange={(v) => update(['coverage', 'plan_type'], v)} />
            <Field label="Member id" value={get(cmJson, ['coverage', 'member_id'])}
                   onChange={(v) => update(['coverage', 'member_id'], v)} />
            <Field label="Sequence" value={get(cmJson, ['coverage', 'coverage_sequence'])}
                   onChange={(v) => update(['coverage', 'coverage_sequence'], v)} />
            <div className="intake-kv__key">Is primary</div>
            <div className="intake-kv__val">
              <label className="intake-check">
                <input
                  type="checkbox"
                  checked={Boolean(get(cmJson, ['coverage', 'is_primary'], false))}
                  onChange={(e) => update(['coverage', 'is_primary'], e.target.checked)}
                />
                Primary coverage
              </label>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'Encounter' && (
        <div className="intake-card">
          <div className="intake-kv">
            <Field label="External id" value={get(cmJson, ['encounter', 'external_id'])}
                   onChange={(v) => update(['encounter', 'external_id'], v)} />
            <Field label="Admission date" value={get(cmJson, ['encounter', 'admission_date'])}
                   onChange={(v) => update(['encounter', 'admission_date'], v)} />
            <Field label="Discharge date" value={get(cmJson, ['encounter', 'discharge_date'])}
                   onChange={(v) => update(['encounter', 'discharge_date'], v)} />
            <Field label="Encounter type" value={get(cmJson, ['encounter', 'encounter_type'])}
                   onChange={(v) => update(['encounter', 'encounter_type'], v)} />
            <Field label="Place of service" value={get(cmJson, ['encounter', 'place_of_service'])}
                   onChange={(v) => update(['encounter', 'place_of_service'], v)} />
            <Field label="Primary dx code" value={get(cmJson, ['encounter', 'primary_dx_code'])}
                   onChange={(v) => update(['encounter', 'primary_dx_code'], v)} />
            <Field label="Primary dx text" value={get(cmJson, ['encounter', 'primary_dx_text'])}
                   onChange={(v) => update(['encounter', 'primary_dx_text'], v)} />
            <Field label="DRG code" value={get(cmJson, ['encounter', 'drg_code'])}
                   onChange={(v) => update(['encounter', 'drg_code'], v)} />
            <Field label="Length of stay (days)" value={get(cmJson, ['encounter', 'length_of_stay_days'])}
                   onChange={(v) => update(['encounter', 'length_of_stay_days'], v)} />
            <Field label="Department" value={get(cmJson, ['encounter', 'department'])}
                   onChange={(v) => update(['encounter', 'department'], v)} />
          </div>
        </div>
      )}

      {activeTab === 'Claim Lines' && (
        <div className="intake-card">
          <div className="intake-actions" style={{ marginBottom: 14 }}>
            <button
              type="button"
              className="intake-btn intake-btn--ghost"
              onClick={() => addItem('claim_lines', {
                line_no: claimLines.length + 1, cpt_or_hcpcs: '', modifiers: [],
                icd10_pointers: [], service_date: '', place_of_service: '',
                billed_amount: '', allowed_amount: '', paid_amount: '',
                denied_amount: '', units: '',
              })}
            >
              + Add line
            </button>
          </div>
          {claimLines.length === 0 ? (
            <div className="intake-empty">No claim lines. Use “Add line” to create one.</div>
          ) : (
            claimLines.map((ln, i) => (
              <div key={i} className="intake-row-card">
                <div className="intake-row-card__fields">
                  <div className="intake-field-inline intake-field-inline--xs">
                    <label>#</label>
                    <input className="intake-input"
                           value={ln.line_no ?? ln.line_number ?? i + 1}
                           onChange={(e) => update(['claim_lines', i, 'line_no'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline">
                    <label>CPT / HCPCS</label>
                    <input className="intake-input"
                           value={ln.cpt_or_hcpcs ?? ln.procedure_code ?? ''}
                           onChange={(e) => update(['claim_lines', i, 'cpt_or_hcpcs'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline">
                    <label>Modifiers</label>
                    <input className="intake-input"
                           value={arrToComma(ln.modifiers)}
                           onChange={(e) => update(['claim_lines', i, 'modifiers'], commaToArr(e.target.value))} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--md">
                    <label>ICD-10 ptrs</label>
                    <input className="intake-input"
                           value={arrToComma(ln.icd10_pointers ?? ln.diagnosis_codes)}
                           onChange={(e) => update(['claim_lines', i, 'icd10_pointers'], commaToArr(e.target.value))} />
                  </div>
                  <div className="intake-field-inline">
                    <label>Service date</label>
                    <input className="intake-input"
                           value={ln.service_date ?? ln.service_date_from ?? ''}
                           onChange={(e) => update(['claim_lines', i, 'service_date'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--xs">
                    <label>POS</label>
                    <input className="intake-input"
                           value={ln.place_of_service ?? ''}
                           onChange={(e) => update(['claim_lines', i, 'place_of_service'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--sm">
                    <label>Billed</label>
                    <input className="intake-input"
                           value={ln.billed_amount ?? ln.charge_amount ?? ''}
                           onChange={(e) => update(['claim_lines', i, 'billed_amount'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--sm">
                    <label>Allowed</label>
                    <input className="intake-input"
                           value={ln.allowed_amount ?? ''}
                           onChange={(e) => update(['claim_lines', i, 'allowed_amount'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--sm">
                    <label>Paid</label>
                    <input className="intake-input"
                           value={ln.paid_amount ?? ''}
                           onChange={(e) => update(['claim_lines', i, 'paid_amount'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--sm">
                    <label>Denied</label>
                    <input className="intake-input"
                           value={ln.denied_amount ?? ''}
                           onChange={(e) => update(['claim_lines', i, 'denied_amount'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--xs">
                    <label>Units</label>
                    <input className="intake-input"
                           value={ln.units ?? ''}
                           onChange={(e) => update(['claim_lines', i, 'units'], e.target.value)} />
                  </div>
                </div>
                <button type="button" className="intake-filelist__remove"
                        onClick={() => removeItem('claim_lines', i)}>
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'Denials' && (
        <div className="intake-card">
          <div className="intake-actions" style={{ marginBottom: 14 }}>
            <button
              type="button"
              className="intake-btn intake-btn--ghost"
              onClick={() => addItem('denials', {
                line_no: '', carc: '', rarc: '', denied_amount: '', payer_note: '',
              })}
            >
              + Add denial
            </button>
          </div>
          {denials.length === 0 ? (
            <div className="intake-empty">No denials. Use “Add denial” to create one.</div>
          ) : (
            denials.map((d, i) => (
              <div key={i} className="intake-row-card">
                <div className="intake-row-card__fields">
                  <div className="intake-field-inline intake-field-inline--xs">
                    <label>Line</label>
                    <input className="intake-input"
                           value={d.line_no ?? ''}
                           onChange={(e) => update(['denials', i, 'line_no'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--sm">
                    <label>CARC</label>
                    <input className="intake-input"
                           value={d.carc ?? ''}
                           onChange={(e) => update(['denials', i, 'carc'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--sm">
                    <label>RARC</label>
                    <input className="intake-input"
                           value={d.rarc ?? ''}
                           onChange={(e) => update(['denials', i, 'rarc'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--sm">
                    <label>Denied</label>
                    <input className="intake-input"
                           value={d.denied_amount ?? ''}
                           onChange={(e) => update(['denials', i, 'denied_amount'], e.target.value)} />
                  </div>
                  <div className="intake-field-inline intake-field-inline--lg">
                    <label>Payer note</label>
                    <input className="intake-input"
                           value={d.payer_note ?? ''}
                           onChange={(e) => update(['denials', i, 'payer_note'], e.target.value)} />
                  </div>
                </div>
                <button type="button" className="intake-filelist__remove"
                        onClick={() => removeItem('denials', i)}>
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'Clinical Evidence' && (
        <div className="intake-card">
          <p className="intake-muted" style={{ marginTop: 0 }}>
            <strong>Required:</strong> at least one clinical note is needed before you can submit.
            This is the only clinical evidence the workflow uses. Uploaded documents are
            reference material for you and are <strong>not</strong> sent to the engine — copy
            anything the appeal needs into an evidence note here.
          </p>
          {!hasClinicalNote && (
            <div className="intake-alert" style={{
              background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a',
              borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13,
            }}>
              Add at least one clinical note (with body text) to enable submission.
            </div>
          )}
          <div className="intake-actions" style={{ margin: '12px 0 16px' }}>
            <button
              type="button"
              className="intake-btn intake-btn--ghost"
              onClick={() => addItem('clinical_evidence', { subject: '', date: '', body: '' })}
            >
              + Add evidence note
            </button>
          </div>
          {clinicalEvidence.length === 0 ? (
            <div className="intake-empty">No evidence notes yet.</div>
          ) : (
            clinicalEvidence.map((ev, i) => (
              <div key={i} className="intake-evidence">
                <div className="intake-evidence__row">
                  <input
                    className="intake-input"
                    style={{ flex: 2 }}
                    placeholder="Subject (e.g. Progress note 2024-03-12)"
                    value={ev.subject ?? ''}
                    onChange={(e) => update(['clinical_evidence', i, 'subject'], e.target.value)}
                  />
                  <input
                    className="intake-input"
                    style={{ flex: 1 }}
                    placeholder="Date (YYYY-MM-DD)"
                    value={ev.date ?? ''}
                    onChange={(e) => update(['clinical_evidence', i, 'date'], e.target.value)}
                  />
                  <button type="button" className="intake-filelist__remove"
                          onClick={() => removeItem('clinical_evidence', i)}>
                    Remove
                  </button>
                </div>
                <textarea
                  className="intake-input intake-textarea"
                  rows={5}
                  placeholder="Evidence text the appeal can cite (labs, vitals, clinical findings, rationale)."
                  value={ev.body ?? ''}
                  onChange={(e) => update(['clinical_evidence', i, 'body'], e.target.value)}
                />
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'Documents' && (
        <div className="intake-card">
          <p className="intake-muted" style={{ marginTop: 0 }}>
            Reference material only. Documents are stored for your review but are{' '}
            <strong>not</strong> sent to the workflow — only the JSON triggers the engine.
            Use the <strong>Clinical Evidence</strong> tab for anything the appeal must use.
          </p>
          <div className="intake-actions" style={{ margin: '12px 0 14px' }}>
            <button type="button" className="intake-btn intake-btn--ghost"
                    onClick={() => docInputRef.current && docInputRef.current.click()}>
              + Add documents
            </button>
            <input ref={docInputRef} type="file" multiple style={{ display: 'none' }}
                   onChange={(e) => { onAddDocuments(e.target.files); e.target.value = ''; }} />
          </div>
          {documents.length === 0 ? (
            <div className="intake-empty">No documents stored.</div>
          ) : (
            documents.map((doc) => (
              <details key={doc.DocumentId || doc.Filename} style={{ marginBottom: 12 }}>
                <summary>
                  <strong>{doc.Filename}</strong>{' '}
                  <span className="intake-filelist__kind">{doc.Kind}</span>{' '}
                  <span className="intake-muted">{doc.ExtractionStatus}</span>
                </summary>
                <div className="intake-doc-text">{doc.ExtractedText || '(no extracted text)'}</div>
              </details>
            ))
          )}
        </div>
      )}

      {activeTab === 'Missing Items' && (
        <div className="intake-card">
          {missingItems.length === 0 ? (
            <div className="intake-empty">Nothing outstanding — this case looks complete.</div>
          ) : (
            missingItems.map((it) => (
              <div key={it.id} className="intake-missing__item">
                <div>
                  <div className="intake-missing__label">
                    {it.label}
                    {it.required ? '' : <span className="intake-muted"> (optional)</span>}
                  </div>
                  <div className="intake-missing__meta">
                    {it.category}{it.code ? ` · CARC ${it.code}` : ''}{it.context ? ` · ${it.context}` : ''}
                  </div>
                </div>
                <div className="intake-missing__controls">
                  <span className={`intake-pill intake-pill--${it.status || 'missing'}`}>{it.status || 'missing'}</span>
                  <select className="intake-input" style={{ minWidth: 130 }}
                          value={it.status || 'missing'}
                          onChange={(e) => setItemStatus(it.id, e.target.value)}>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            ))
          )}
          <p className="intake-muted" style={{ marginTop: 12 }}>
            Use <strong>Save</strong> to persist your responses. Required items must be
            resolved before you can submit.
          </p>
        </div>
      )}

      {activeTab === 'Raw JSON' && (
        <div className="intake-card">
          {rawError && <div className="intake-banner intake-banner--error">{rawError}</div>}
          <textarea
            className="intake-json"
            value={rawText}
            spellCheck={false}
            onChange={(e) => setRawText(e.target.value)}
          />
          <p className="intake-muted">Edits here are validated and applied when you click Save.</p>
        </div>
      )}
    </div>
  );
}
